import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  LinearProgress,
  IconButton,
  Button,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceDot,
  ReferenceLine,
} from 'recharts';
import apiClient from '../api/client';
import { triggerEval } from '../api/runs';
import { Run, RunMetrics, Task, ArtifactInfo, RewardEntry } from '../types';
import { useIterationMap } from '../hooks/useIterationMap';
import { usePresignedUrlCache } from '../hooks/usePresignedUrlCache';
import { extractIteration, formatBytes } from '../utils/artifacts';
import { getGpuHourlyRate } from '../constants/training';

const SWEEP_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FF6B6B'];

interface TrainingRunDetailsProps {
  run: Run;
  apiBasePath?: string;
}

// Custom tooltip for the interactive chart
const ChartTooltip: React.FC<{
  active?: boolean;
  payload?: any[];
  label?: any;
  videoIterations: number[];
  checkpointIterations: number[];
  pendingEvalIterations: number[];
}> = ({ active, payload, label, videoIterations, checkpointIterations, pendingEvalIterations }) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0].payload;
  const hasVideo = videoIterations.includes(entry.iteration);
  const hasCheckpoint = checkpointIterations.includes(entry.iteration);
  const isPendingEval = pendingEvalIterations.includes(entry.iteration);

  return (
    <Box
      sx={{
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(255,255,255,0.2)',
        p: 1.5,
        minWidth: 140,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Iteration {entry.iteration}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
        Reward: {entry.mean_reward?.toFixed(2)}
      </Typography>
      {entry.mean_episode_length != null && (
        <Typography variant="body2" sx={{ color: '#82ca9d' }}>
          Ep Length: {entry.mean_episode_length?.toFixed(0)}
        </Typography>
      )}
      {hasVideo && (
        <Typography variant="caption" sx={{ color: '#6bcfff', display: 'block', mt: 0.5 }}>
          Video available
        </Typography>
      )}
      {isPendingEval && (
        <Typography variant="caption" sx={{ color: '#6bcfff', display: 'block', mt: hasVideo ? 0 : 0.5 }}>
          Eval pending...
        </Typography>
      )}
      {hasCheckpoint && (
        <Typography variant="caption" sx={{ color: '#51cf66', display: 'block', mt: (hasVideo || isPendingEval) ? 0 : 0.5 }}>
          Checkpoint available
        </Typography>
      )}
    </Box>
  );
};

const TrainingRunDetails: React.FC<TrainingRunDetailsProps> = ({ run, apiBasePath }) => {
  const basePath = apiBasePath || `/api/v1/runs/${run.slug}`;

  // Cancel state
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    if (!window.confirm('Cancel this run? Running tasks will be stopped.')) return;
    setCancelling(true);
    try {
      await apiClient.post(`/api/v1/runs/${run.slug}/cancel`);
    } catch (err) {
      console.error('Failed to cancel run:', err);
    } finally {
      setCancelling(false);
    }
  };

  // Pause/Resume state
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [resuming, setResuming] = useState(false);

  const handlePause = async () => {
    setPausing(true);
    try {
      await apiClient.post(`/api/v1/runs/${run.slug}/pause`);
    } catch (err) {
      console.error('Failed to pause run:', err);
    } finally {
      setPausing(false);
      setPauseDialogOpen(false);
    }
  };

  const handleResume = async () => {
    setResuming(true);
    try {
      await apiClient.post(`/api/v1/runs/${run.slug}/resume`);
    } catch (err) {
      console.error('Failed to resume run:', err);
    } finally {
      setResuming(false);
    }
  };

  // State
  const [metrics, setMetrics] = useState<RunMetrics | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [artifacts, setArtifacts] = useState<ArtifactInfo[]>([]);
  const [rewardHistory, setRewardHistory] = useState<RewardEntry[]>([]);
  const [selectedIteration, setSelectedIteration] = useState<number | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const sweepVideoKeyRef = useRef<string | null>(null);
  const sweepVideoDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [evalGenerating, setEvalGenerating] = useState(false);
  const [evalError, setEvalError] = useState<string | null>(null);
  // Ticking clock — forces re-render every second so elapsed time updates live
  const [now, setNow] = useState(Date.now());
  // Refs
  const sweepHistoryRef = useRef<Record<number, RewardEntry[]>>({});
  const videoDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentVideoKeyRef = useRef<string | null>(null);

  // Hooks
  const getPresignedUrl = usePresignedUrlCache(basePath);
  const iterMap = useIterationMap(artifacts, rewardHistory);

  const isSweep = (run.config.sweep_configs?.length ?? 0) > 0;
  const trainingTasks = tasks.filter((t) => t.params?.task_type !== 'evaluation');
  const isActive = run.status === 'running' || run.status === 'queued';
  const isPaused = run.status === 'paused';
  const isCompleted = run.status === 'completed';
  const training = metrics?.training;
  const progressPercent = Math.min(100, Math.max(0, training?.progress_percent ?? 0));
  const pendingEvalIters: number[] = (training as any)?.pending_eval_iterations ?? [];

  // --- Ticking clock for live elapsed time ---
  useEffect(() => {
    if (!isActive) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isActive]);

  // --- Cost & ETA calculations ---

  const gpuCount = run.config.training_config?.gpu_count ?? 1;
  const gpuType = run.config.fleet?.gpu_type ?? 'RTX 4090';
  const maxWorkers = run.config.fleet?.max_workers ?? 1;
  const baseRate = getGpuHourlyRate(gpuType);
  const hourlyRate = gpuCount * baseRate * maxWorkers;

  // Whether training has actually started producing data
  const hasTrainingData = training && training.current_iteration > 0;

  const elapsedMs = (() => {
    const endTime = (isCompleted || run.status === 'failed' || isPaused)
      ? new Date(run.updated_at).getTime()
      : now;
    return Math.max(0, endTime - new Date(run.created_at).getTime());
  })();

  const costSoFar = Math.max(0, (elapsedMs / 3600000) * hourlyRate);

  const etaDisplay = (() => {
    if (!isActive || !training) return null;
    const currentIter = training.current_iteration;
    const maxIter = training.max_iterations;
    if (currentIter < 2 || elapsedMs === 0) return '--';
    const remainingIters = maxIter - currentIter;
    if (remainingIters <= 0) return '--';
    const avgIterMs = elapsedMs / currentIter;
    const etaMs = remainingIters * avgIterMs;
    const etaH = Math.floor(etaMs / 3600000);
    const etaM = Math.floor((etaMs % 3600000) / 60000);
    if (etaH > 0) return `~${etaH}h ${etaM}m`;
    return `~${etaM}m`;
  })();

  // --- Data fetching ---

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await apiClient.get(`${basePath}/metrics`);
      setMetrics(res.data);
      if (res.data.training?.reward_history) {
        const incoming = res.data.training.reward_history;
        setRewardHistory((prev) => {
          if (
            prev.length === incoming.length &&
            prev.length > 0 &&
            prev[prev.length - 1].iteration === incoming[incoming.length - 1].iteration
          ) {
            return prev; // same data, keep reference stable
          }
          return incoming;
        });
      }
    } catch (e) {
      console.error('Failed to fetch metrics:', e);
    }
  }, [basePath]);

  const fetchTasks = useCallback(async () => {
    try {
      const res = await apiClient.get(`${basePath}/tasks`);
      const taskData: Task[] = res.data;
      setTasks(taskData);
      if (isSweep) {
        for (const t of taskData) {
          const tp = t.metrics?.training_progress;
          if (tp?.reward_history) {
            sweepHistoryRef.current[t.id] = tp.reward_history;
          }
        }
      }
    } catch (e) {
      console.error('Failed to fetch tasks:', e);
    }
  }, [basePath, isSweep]);

  const fetchArtifacts = useCallback(async () => {
    try {
      const res = await apiClient.get(`${basePath}/artifacts`);
      setArtifacts(res.data.artifacts);
    } catch (e) {
      console.error('Failed to fetch artifacts:', e);
    }
  }, [basePath]);

  useEffect(() => {
    fetchMetrics();
    fetchTasks();
    fetchArtifacts();
    if (!isActive) return;
    const mi = setInterval(fetchMetrics, 5000);
    const ai = setInterval(fetchArtifacts, 30000);
    const ti = setInterval(fetchTasks, 10000);
    return () => {
      clearInterval(mi);
      clearInterval(ai);
      clearInterval(ti);
    };
  }, [fetchMetrics, fetchTasks, fetchArtifacts, isActive]);

  // Default selectedIteration to latest
  useEffect(() => {
    if (selectedIteration === null && rewardHistory.length > 0) {
      setSelectedIteration(rewardHistory[rewardHistory.length - 1].iteration);
    }
  }, [rewardHistory, selectedIteration]);

  // --- Debounced video fetch ---

  useEffect(() => {
    if (selectedIteration === null || isSweep) return;

    const nearest = iterMap.nearestVideo(selectedIteration);
    if (!nearest) {
      if (currentVideoKeyRef.current !== null) {
        setVideoUrl(null);
        currentVideoKeyRef.current = null;
      }
      return;
    }

    if (nearest.s3_key === currentVideoKeyRef.current) return;

    if (videoDebounceRef.current) clearTimeout(videoDebounceRef.current);
    videoDebounceRef.current = setTimeout(async () => {
      currentVideoKeyRef.current = nearest.s3_key;
      try {
        const url = await getPresignedUrl(nearest.s3_key);
        setVideoUrl(url);
      } catch (e) {
        console.error('Failed to fetch video URL:', e);
      }
    }, 300);

    return () => {
      if (videoDebounceRef.current) clearTimeout(videoDebounceRef.current);
    };
  }, [selectedIteration, iterMap, isSweep, getPresignedUrl]);

  // --- Helpers ---

  const handleDownload = async (s3Key: string) => {
    try {
      const url = await getPresignedUrl(s3Key);
      window.open(url, '_blank');
    } catch (e) {
      console.error('Failed to get download URL:', e);
    }
  };

  const goToPrevVideo = () => {
    const iters = iterMap.videoIterations;
    if (iters.length === 0) return;
    if (selectedIteration === null) {
      setSelectedIteration(iters[iters.length - 1]);
      return;
    }
    // Find the last iteration strictly before the current one
    let prev = iters[0];
    for (const i of iters) {
      if (i >= selectedIteration) break;
      prev = i;
    }
    setSelectedIteration(prev);
  };

  const goToNextVideo = () => {
    const iters = iterMap.videoIterations;
    if (iters.length === 0) return;
    if (selectedIteration === null) {
      setSelectedIteration(iters[0]);
      return;
    }
    // Find the first iteration strictly after the current one
    for (const i of iters) {
      if (i > selectedIteration) {
        setSelectedIteration(i);
        return;
      }
    }
    // Already at last, stay
  };

  const currentVideoIndex = (() => {
    if (selectedIteration === null) return -1;
    const iters = iterMap.videoIterations;
    // Find which video we're at or nearest before
    let idx = -1;
    for (let i = 0; i < iters.length; i++) {
      if (iters[i] <= selectedIteration) idx = i;
      else break;
    }
    return idx;
  })();

  const preloadAdjacentVideos = useCallback(() => {
    const iters = iterMap.videoIterations;
    const idx = currentVideoIndex;
    const toPreload: number[] = [];
    if (idx > 0) toPreload.push(iters[idx - 1]);
    if (idx < iters.length - 1) toPreload.push(iters[idx + 1]);
    for (const iter of toPreload) {
      const artifact = iterMap.nearestVideo(iter);
      if (artifact) {
        getPresignedUrl(artifact.s3_key)
          .then((url) => { fetch(url).catch(() => {}); })
          .catch(() => {});
      }
    }
  }, [iterMap, currentVideoIndex, getPresignedUrl]);

  // --- Derived data ---

  const nearestVideo =
    selectedIteration !== null ? iterMap.nearestVideo(selectedIteration) : null;
  const nearestCheckpoint =
    selectedIteration !== null ? iterMap.nearestCheckpoint(selectedIteration) : null;
  const nearestVideoIter = nearestVideo ? extractIteration(nearestVideo.filename) : null;
  const nearestCpIter = nearestCheckpoint
    ? extractIteration(nearestCheckpoint.filename)
    : null;

  const tensorboardArtifact = artifacts.find((a) => a.artifact_type === 'tensorboard');

  // Latest checkpoint for download banner
  const latestCheckpoint = artifacts
    .filter((a) => a.artifact_type === 'checkpoint')
    .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))[0] || null;

  // Latest checkpoint iteration (used in pause dialog)
  const lastCheckpointIter = latestCheckpoint ? extractIteration(latestCheckpoint.filename) : null;

  // Sweep helpers
  const buildSweepChartData = () => {
    // Collect per-task histories and all unique iterations
    const allIterations = new Set<number>();
    const taskHistories: Record<string, { iteration: number; mean_reward: number }[]> = {};

    for (const t of trainingTasks) {
      const history = sweepHistoryRef.current[t.id] || [];
      const label = t.params.sweep_label || `task-${t.id}`;
      taskHistories[label] = history.map((e) => ({
        iteration: e.iteration,
        mean_reward: e.mean_reward,
      }));
      for (const entry of history) {
        allIterations.add(entry.iteration);
      }
    }

    const sortedIters = Array.from(allIterations).sort((a, b) => a - b);

    // Build rows with carry-forward so each task has a continuous line
    const lastKnown: Record<string, number | null> = {};
    return sortedIters.map((iter) => {
      const row: Record<string, any> = { iteration: iter };
      for (const [label, history] of Object.entries(taskHistories)) {
        const entry = history.find((e) => e.iteration === iter);
        if (entry) {
          lastKnown[label] = entry.mean_reward;
        }
        if (lastKnown[label] != null) {
          row[label] = lastKnown[label];
        }
      }
      return row;
    });
  };

  const sweepLabels = isSweep
    ? trainingTasks.map((t) => t.params.sweep_label || `task-${t.id}`)
    : [];

  const leaderboardTasks = [...trainingTasks].sort((a, b) => {
    const ra = a.metrics?.training_progress?.mean_reward ?? -Infinity;
    const rb = b.metrics?.training_progress?.mean_reward ?? -Infinity;
    return rb - ra;
  });

  // --- Sweep: auto-select best task ---
  useEffect(() => {
    if (!isSweep || trainingTasks.length === 0) return;
    if (selectedTaskId !== null && trainingTasks.some((t) => t.id === selectedTaskId)) return;
    // Default to the task with the highest mean_reward
    const best = [...trainingTasks].sort((a, b) => {
      const ra = a.metrics?.training_progress?.mean_reward ?? -Infinity;
      const rb = b.metrics?.training_progress?.mean_reward ?? -Infinity;
      return rb - ra;
    })[0];
    if (best) setSelectedTaskId(best.id);
  }, [isSweep, trainingTasks, selectedTaskId]);

  // --- Sweep: selected task derived data ---
  const selectedTask = useMemo(
    () => tasks.find((t) => t.id === selectedTaskId) ?? null,
    [tasks, selectedTaskId],
  );

  const selectedTaskProgress = selectedTask?.metrics?.training_progress ?? null;

  const selectedTaskArtifacts = useMemo(
    () =>
      selectedTaskId != null
        ? artifacts.filter((a) => a.s3_key.includes(`task-${selectedTaskId}/`))
        : [],
    [artifacts, selectedTaskId],
  );

  const selectedTaskRewardHistory = useMemo(
    () =>
      selectedTaskId != null
        ? sweepHistoryRef.current[selectedTaskId] ?? []
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedTaskId, tasks], // tasks triggers recalc when sweep histories update via ref
  );

  // Sweep per-task iteration map
  const sweepIterMap = useIterationMap(selectedTaskArtifacts, selectedTaskRewardHistory);

  // Sweep per-task selected iteration — reset when task changes
  const [sweepSelectedIteration, setSweepSelectedIteration] = useState<number | null>(null);

  useEffect(() => {
    if (!isSweep) return;
    if (selectedTaskRewardHistory.length > 0) {
      setSweepSelectedIteration(
        selectedTaskRewardHistory[selectedTaskRewardHistory.length - 1].iteration,
      );
    } else {
      setSweepSelectedIteration(null);
    }
  }, [isSweep, selectedTaskId, selectedTaskRewardHistory]);

  // Sweep: debounced video fetch for selected task
  const [sweepVideoUrl, setSweepVideoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isSweep || sweepSelectedIteration === null) return;

    const nearest = sweepIterMap.nearestVideo(sweepSelectedIteration);
    if (!nearest) {
      if (sweepVideoKeyRef.current !== null) {
        setSweepVideoUrl(null);
        sweepVideoKeyRef.current = null;
      }
      return;
    }

    if (nearest.s3_key === sweepVideoKeyRef.current) return;

    if (sweepVideoDebounceRef.current) clearTimeout(sweepVideoDebounceRef.current);
    sweepVideoDebounceRef.current = setTimeout(async () => {
      sweepVideoKeyRef.current = nearest.s3_key;
      try {
        const url = await getPresignedUrl(nearest.s3_key);
        setSweepVideoUrl(url);
      } catch (e) {
        console.error('Failed to fetch sweep video URL:', e);
      }
    }, 300);

    return () => {
      if (sweepVideoDebounceRef.current) clearTimeout(sweepVideoDebounceRef.current);
    };
  }, [isSweep, sweepSelectedIteration, sweepIterMap, getPresignedUrl]);

  // Reset sweep video and eval state when task changes
  useEffect(() => {
    if (isSweep) {
      setSweepVideoUrl(null);
      sweepVideoKeyRef.current = null;
      setEvalGenerating(false);
      setEvalError(null);
    }
  }, [isSweep, selectedTaskId]);

  // Clear eval-generating state when a video arrives via polling
  useEffect(() => {
    if (sweepIterMap.videoIterations.length > 0 && evalGenerating) {
      setEvalGenerating(false);
    }
  }, [sweepIterMap.videoIterations.length, evalGenerating]);

  // Sweep video navigation helpers
  const sweepGoToPrevVideo = () => {
    const iters = sweepIterMap.videoIterations;
    if (iters.length === 0) return;
    if (sweepSelectedIteration === null) {
      setSweepSelectedIteration(iters[iters.length - 1]);
      return;
    }
    let prev = iters[0];
    for (const i of iters) {
      if (i >= sweepSelectedIteration) break;
      prev = i;
    }
    setSweepSelectedIteration(prev);
  };

  const sweepGoToNextVideo = () => {
    const iters = sweepIterMap.videoIterations;
    if (iters.length === 0) return;
    if (sweepSelectedIteration === null) {
      setSweepSelectedIteration(iters[0]);
      return;
    }
    for (const i of iters) {
      if (i > sweepSelectedIteration) {
        setSweepSelectedIteration(i);
        return;
      }
    }
  };

  const sweepCurrentVideoIndex = (() => {
    if (sweepSelectedIteration === null) return -1;
    const iters = sweepIterMap.videoIterations;
    let idx = -1;
    for (let i = 0; i < iters.length; i++) {
      if (iters[i] <= sweepSelectedIteration) idx = i;
      else break;
    }
    return idx;
  })();

  const sweepNearestVideo =
    sweepSelectedIteration !== null ? sweepIterMap.nearestVideo(sweepSelectedIteration) : null;
  const sweepNearestCheckpoint =
    sweepSelectedIteration !== null ? sweepIterMap.nearestCheckpoint(sweepSelectedIteration) : null;
  const sweepNearestVideoIter = sweepNearestVideo ? extractIteration(sweepNearestVideo.filename) : null;
  const sweepNearestCpIter = sweepNearestCheckpoint ? extractIteration(sweepNearestCheckpoint.filename) : null;

  const sweepTensorboardArtifact = selectedTaskArtifacts.find((a) => a.artifact_type === 'tensorboard');

  const sweepLatestCheckpoint = selectedTaskArtifacts
    .filter((a) => a.artifact_type === 'checkpoint')
    .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))[0] || null;

  // Sweep cost/ETA — use run.created_at as baseline (pod is running from run start)
  const sweepRunElapsedMs = (() => {
    if (!run) return 0;
    const createdAt = new Date(run.created_at).getTime();
    if (isPaused) {
      return Math.max(0, new Date(run.updated_at).getTime() - createdAt);
    }
    // Use run-level status (isActive) as primary signal — tasks array may not be
    // loaded yet on the render where stat cards first appear.
    if (isActive) {
      return Math.max(0, now - createdAt);
    }
    // Run is done — use the latest task timestamp as end time
    const endTime = tasks.length > 0
      ? new Date(
          tasks.reduce((latest, t) => {
            const ts = t.completed_at ?? t.created_at;
            return ts > latest ? ts : latest;
          }, run.created_at)
        ).getTime()
      : new Date(run.updated_at).getTime();
    return Math.max(0, endTime - createdAt);
  })();

  const sweepTaskElapsedMs = sweepRunElapsedMs;

  const sweepTaskCost = Math.max(0, (sweepRunElapsedMs / 3600000) * hourlyRate);

  const sweepEtaDisplay = (() => {
    if (!selectedTask || selectedTask.status !== 'running' || !selectedTaskProgress) return null;
    const currentIter = selectedTaskProgress.current_iteration;
    const maxIter = selectedTaskProgress.max_iterations;
    if (currentIter < 2 || sweepRunElapsedMs === 0) return '--';
    const remainingIters = maxIter - currentIter;
    if (remainingIters <= 0) return '--';
    // Use task-level elapsed for per-iteration timing (avoids including startup
    // overhead from run creation to when this specific task started training)
    const taskElapsedMs = Math.max(0, now - new Date(selectedTask.created_at).getTime());
    if (taskElapsedMs === 0) return '--';
    const avgIterMs = taskElapsedMs / currentIter;
    const etaMs = remainingIters * avgIterMs;
    const etaH = Math.floor(etaMs / 3600000);
    const etaM = Math.floor((etaMs % 3600000) / 60000);
    if (etaH > 0) return `~${etaH}h ${etaM}m`;
    return `~${etaM}m`;
  })();

  const selectedTaskIndex = trainingTasks.findIndex((t) => t.id === selectedTaskId);

  const preloadSweepAdjacentVideos = useCallback(() => {
    const iters = sweepIterMap.videoIterations;
    const idx = sweepCurrentVideoIndex;
    const toPreload: number[] = [];
    if (idx > 0) toPreload.push(iters[idx - 1]);
    if (idx < iters.length - 1) toPreload.push(iters[idx + 1]);
    for (const iter of toPreload) {
      const artifact = sweepIterMap.nearestVideo(iter);
      if (artifact) {
        getPresignedUrl(artifact.s3_key)
          .then((url) => { fetch(url).catch(() => {}); })
          .catch(() => {});
      }
    }
  }, [sweepIterMap, sweepCurrentVideoIndex, getPresignedUrl]);

  // --- Stat card helper ---

  const StatCard: React.FC<{ value: string; label: string }> = ({ value, label }) => (
    <Box
      sx={{
        flex: 1,
        minWidth: 120,
        backgroundColor: '#0a0a0a',
        border: '1px solid rgba(255,255,255,0.08)',
        p: 2,
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 600, color: '#fff', mb: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );

  // --- Render: Sweep mode ---

  // Whether any sweep task has produced training data
  const anySweepTaskHasData = trainingTasks.some(
    (t) => t.metrics?.training_progress && t.metrics.training_progress.current_iteration > 0,
  );

  // Collect error messages from failed tasks
  const failedTaskErrors = tasks
    .filter((t) => t.status === 'failed' && t.error_message)
    .map((t) => ({ label: t.params.sweep_label || `Task ${t.id}`, error: t.error_message! }));

  if (isSweep) {
    const selectedLabel = selectedTask?.params.sweep_label || (selectedTask ? `task-${selectedTask.id}` : '');
    const selectedTaskIsActive = selectedTask?.status === 'running' || selectedTask?.status === 'queued';
    const selectedTaskIsCompleted = selectedTask?.status === 'completed';
    const selectedTaskHasData = selectedTaskProgress && selectedTaskProgress.current_iteration > 0;

    return (
      <Box
        sx={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          '& .MuiTypography-root, & .MuiButton-root, & .MuiChip-label, & .MuiSelect-select, & .MuiMenuItem-root': {
            fontFamily: 'inherit',
          },
        }}
      >
        {/* Progress bar */}
        {isActive && (
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{ height: 4, borderRadius: 2, mb: 3 }}
          />
        )}

        {/* Status row + pause/cancel/resume */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Chip
            label={run.status}
            color={run.status === 'completed' ? 'success' : run.status === 'failed' ? 'error' : 'info'}
            size="small"
            sx={isPaused ? { backgroundColor: '#ffca28', color: '#000' } : undefined}
          />
          {training && (
            <Typography variant="body2" color="text.secondary">
              iter {training.current_iteration}/{training.max_iterations} &middot;{' '}
              {trainingTasks.length} configs
            </Typography>
          )}
          {isPaused && (
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={handleResume}
              disabled={resuming}
              sx={{ ml: 'auto', backgroundColor: '#51cf66', color: '#000', fontWeight: 600, '&:hover': { backgroundColor: '#40c057' } }}
            >
              {resuming ? 'Resuming...' : 'Resume'}
            </Button>
          )}
          {isActive && (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PauseCircleOutlineIcon />}
                onClick={() => setPauseDialogOpen(true)}
                disabled={pausing}
                sx={{ ml: 'auto', borderColor: '#ffca28', color: '#ffca28', '&:hover': { borderColor: '#ffc107', backgroundColor: 'rgba(255,202,40,0.08)' } }}
              >
                Pause
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<CancelOutlinedIcon />}
                onClick={handleCancel}
                disabled={cancelling}
                sx={{ borderColor: '#ff6b6b', color: '#ff6b6b', '&:hover': { borderColor: '#ff4444', backgroundColor: 'rgba(255,107,107,0.08)' } }}
              >
                {cancelling ? 'Cancelling...' : 'Cancel'}
              </Button>
            </>
          )}
        </Box>

        {/* Failed run — show error messages */}
        {run.status === 'failed' && failedTaskErrors.length > 0 && (
          <Box
            sx={{
              p: 2.5,
              mb: 3,
              backgroundColor: 'rgba(255, 107, 107, 0.06)',
              border: '1px solid rgba(255, 107, 107, 0.25)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff6b6b', mb: 1 }}>
              Run failed
            </Typography>
            {failedTaskErrors.slice(0, 3).map((te, i) => (
              <Typography key={i} variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5 }}>
                <Box component="span" sx={{ color: 'rgba(255,255,255,0.4)' }}>{te.label}:</Box>{' '}
                {te.error}
              </Typography>
            ))}
            {failedTaskErrors.length > 3 && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                +{failedTaskErrors.length - 3} more failed task{failedTaskErrors.length - 3 !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
        )}

        {/* Starting up — no task has data yet */}
        {isActive && !anySweepTaskHasData && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
            }}
          >
            <CircularProgress size={40} sx={{ color: '#EF8997', mb: 2.5 }} />
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#fff', mb: 0.5 }}>
              Starting up
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
              Booting Isaac Sim across {trainingTasks.length} configs...
            </Typography>
          </Box>
        )}

        {/* Main content — only when we have data or run is done */}
        {(anySweepTaskHasData || !isActive) && (<>

          {/* Sweep reward comparison chart */}
          <Card
            sx={{
              backgroundColor: '#0a0a0a',
              border: '1px solid rgba(255,255,255,0.12)',
              mb: 3,
            }}
          >
            <CardContent sx={{ pb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.5)', mb: 2, letterSpacing: '0.05em', fontSize: 11, textTransform: 'uppercase' }}>
                Reward Comparison
              </Typography>
              <Box sx={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  <LineChart data={buildSweepChartData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="iteration" type="number" stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 11 }} domain={[0, 'auto']} />
                    <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 11 }} domain={[0, 'auto']} />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid rgba(255,255,255,0.2)',
                      }}
                    />
                    <Legend />
                    {sweepLabels.map((label, i) => {
                      const isSelected = label === selectedLabel;
                      return (
                        <Line
                          key={label}
                          type="monotone"
                          dataKey={label}
                          stroke={SWEEP_COLORS[i % SWEEP_COLORS.length]}
                          dot={false}
                          strokeWidth={isSelected ? 3 : 2}
                          strokeOpacity={isSelected ? 1 : 0.7}
                          connectNulls
                          isAnimationActive={false}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          {/* Config selector dropdown */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <Select
                value={selectedTaskId !== null ? String(selectedTaskId) : ''}
                onChange={(e: SelectChangeEvent) => {
                  const id = Number(e.target.value);
                  setSelectedTaskId(id);
                }}
                IconComponent={KeyboardArrowDownIcon}
                displayEmpty
                renderValue={() => {
                  if (!selectedTask) return 'Select config...';
                  const reward = selectedTaskProgress?.mean_reward;
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {selectedLabel}
                      </Typography>
                      {reward != null && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                          reward {reward.toFixed(1)}
                        </Typography>
                      )}
                    </Box>
                  );
                }}
                sx={{
                  backgroundColor: '#0a0a0a',
                  border: '1px solid rgba(255,255,255,0.12)',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiSelect-select': { py: 1.25, px: 2 },
                  '&:hover': { borderColor: 'rgba(255,255,255,0.25)' },
                  transition: 'border-color 0.15s',
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: '#141414',
                      border: '1px solid rgba(255,255,255,0.12)',
                      mt: 0.5,
                      '& .MuiMenuItem-root': {
                        py: 1.25,
                        px: 2,
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
                        '&.Mui-selected': { backgroundColor: 'rgba(239, 137, 151, 0.08)' },
                        '&.Mui-selected:hover': { backgroundColor: 'rgba(239, 137, 151, 0.12)' },
                      },
                    },
                  },
                }}
              >
                {leaderboardTasks.map((t, idx) => {
                  const tp = t.metrics?.training_progress;
                  const label = t.params.sweep_label || `task-${t.id}`;
                  return (
                    <MenuItem key={t.id} value={String(t.id)}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: t.id === selectedTaskId ? 600 : 400,
                            flex: 1,
                          }}
                        >
                          {idx === 0 && trainingTasks.length > 1 ? `${label} (best)` : label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', minWidth: 70, textAlign: 'right' }}>
                          {tp?.mean_reward != null ? `reward ${tp.mean_reward.toFixed(1)}` : 'no data'}
                        </Typography>
                        <Chip
                          label={t.status}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 11,
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              Viewing config {selectedTaskIndex + 1} of {trainingTasks.length}
            </Typography>
          </Box>

          {/* Download button — selected task completed + has checkpoint */}
          {selectedTaskIsCompleted && sweepLatestCheckpoint && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => handleDownload(sweepLatestCheckpoint.s3_key)}
                sx={{
                  backgroundColor: '#51cf66',
                  color: '#000',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: '#40c057' },
                }}
              >
                Download Policy
              </Button>
            </Box>
          )}

          {/* Stat cards for selected task */}
          {(selectedTaskHasData || (selectedTask && !selectedTaskIsActive)) && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {selectedTaskIsActive ? (
              <>
                <StatCard
                  value={(() => {
                    const h = Math.floor(sweepTaskElapsedMs / 3600000);
                    const m = Math.floor((sweepTaskElapsedMs % 3600000) / 60000);
                    return h > 0 ? `${h}h ${m}m` : `${Math.max(0, m)}m`;
                  })()}
                  label="Time Running"
                />
                <StatCard
                  value={selectedTaskProgress ? `${selectedTaskProgress.progress_percent.toFixed(0)}%` : '--'}
                  label="Iteration"
                />
              </>
            ) : (
              <>
                <StatCard
                  value={new Date(selectedTask!.created_at).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}
                  label="Started"
                />
                <StatCard
                  value={(() => {
                    const h = sweepTaskElapsedMs / 3600000;
                    return h >= 1 ? `${h.toFixed(1)}h` : `${Math.max(0, Math.round(h * 60))}m`;
                  })()}
                  label="Train Time"
                />
              </>
            )}
            <StatCard
              value={selectedTaskProgress?.mean_reward?.toFixed(2) ?? '--'}
              label="Mean Reward"
            />
            <StatCard
              value={selectedTaskProgress?.mean_episode_length?.toFixed(0) ?? '--'}
              label="Ep Length"
            />
            <StatCard
              value={`$${sweepTaskCost.toFixed(2)}`}
              label={selectedTaskIsActive ? 'Run Cost' : 'Total Cost'}
            />
            {selectedTaskIsActive && sweepEtaDisplay && (
              <StatCard
                value={sweepEtaDisplay}
                label="ETA"
              />
            )}
          </Box>
          )}

          {/* Video + Action Cards for selected task */}
          <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 3 }}>
            {/* Video — 2/3 */}
            <Box sx={{ flex: '0 0 66%', minWidth: 0 }}>
              <Card
                sx={{
                  backgroundColor: '#0a0a0a',
                  border: '1px solid rgba(255,255,255,0.08)',
                  overflow: 'hidden',
                }}
              >
                <CardContent sx={{ p: '0 !important' }}>
                  {sweepVideoUrl ? (
                    <video
                      key={sweepVideoUrl}
                      src={sweepVideoUrl}
                      controls
                      autoPlay
                      muted
                      loop
                      style={{ width: '100%', display: 'block' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        aspectRatio: '16/9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 1.5,
                        backgroundColor: 'rgba(255,255,255,0.02)',
                      }}
                    >
                      {selectedTaskIsActive ? (
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 500 }}>
                          {(() => {
                            const evalInterval = run.config.training_config?.eval_interval || run.config.training_config?.max_iterations;
                            const currentIter = selectedTaskProgress?.current_iteration ?? 0;
                            return evalInterval && currentIter < evalInterval
                              ? `First video at iteration ${evalInterval}`
                              : 'Rendering eval video...';
                          })()}
                        </Typography>
                      ) : evalGenerating ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                          <CircularProgress size={28} sx={{ color: 'rgba(255,255,255,0.5)' }} />
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>
                            Generating eval video...
                          </Typography>
                        </Box>
                      ) : sweepIterMap.checkpointIterations.length > 0 && selectedTask ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PlayArrowIcon />}
                            onClick={async () => {
                              const latestCpIter = sweepIterMap.checkpointIterations[
                                sweepIterMap.checkpointIterations.length - 1
                              ];
                              setEvalGenerating(true);
                              setEvalError(null);
                              try {
                                await triggerEval(run.slug, latestCpIter, selectedTask.id);
                              } catch (err: any) {
                                const detail = err?.response?.data?.detail;
                                if (err?.response?.status === 409) {
                                  // Eval already exists -- treat as success, it will appear via polling
                                } else {
                                  setEvalError(detail || 'Failed to trigger eval');
                                  setEvalGenerating(false);
                                }
                              }
                            }}
                            sx={{
                              borderColor: 'rgba(255,255,255,0.2)',
                              color: 'rgba(255,255,255,0.7)',
                              textTransform: 'none',
                              fontWeight: 500,
                              '&:hover': {
                                borderColor: 'rgba(255,255,255,0.4)',
                                backgroundColor: 'rgba(255,255,255,0.05)',
                              },
                            }}
                          >
                            Generate Video
                          </Button>
                          {evalError && (
                            <Typography variant="caption" sx={{ color: '#ff6b6b' }}>
                              {evalError}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)' }}>
                            Checkpoint at iteration {sweepIterMap.checkpointIterations[sweepIterMap.checkpointIterations.length - 1]}
                          </Typography>
                        </Box>
                      ) : (
                        <Tooltip
                          title="Videos are generated at eval checkpoints (eval_interval). If your run finished before the first eval checkpoint, no video was created."
                          arrow
                          placement="top"
                        >
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 500, cursor: 'help' }}>
                            No videos for this config
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Video navigation */}
              {sweepIterMap.videoIterations.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  <IconButton
                    size="small"
                    onClick={sweepGoToPrevVideo}
                    onMouseEnter={preloadSweepAdjacentVideos}
                    disabled={sweepCurrentVideoIndex <= 0}
                    sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}
                  >
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, textAlign: 'center' }}>
                    {sweepCurrentVideoIndex >= 0
                      ? `${sweepCurrentVideoIndex + 1} / ${sweepIterMap.videoIterations.length}`
                      : `${sweepIterMap.videoIterations.length} videos`}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={sweepGoToNextVideo}
                    onMouseEnter={preloadSweepAdjacentVideos}
                    disabled={sweepCurrentVideoIndex >= sweepIterMap.videoIterations.length - 1}
                    sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                  {sweepNearestVideoIter !== null && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', ml: 1 }}>
                      {sweepNearestVideoIter === sweepSelectedIteration
                        ? `Iteration ${sweepNearestVideoIter}`
                        : `Nearest at iteration ${sweepNearestVideoIter}`}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            {/* Action cards — 1/3 */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* W&B */}
              {run.wandb_url && (
                <Box
                  component="a"
                  href={run.wandb_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textDecoration: 'none',
                    color: 'inherit',
                    backgroundColor: '#0a0a0a',
                    border: '1px solid rgba(255,255,255,0.08)',
                    p: 2.5,
                    transition: 'border-color 0.15s',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.2)' },
                    display: 'block',
                  }}
                >
                  <img src="/wandb-logo.png" alt="" style={{ width: 24, height: 24, marginBottom: 12 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: '-0.01em', mb: 0.5 }}>
                    Weights & Biases
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    View run dashboard
                  </Typography>
                </Box>
              )}
              {/* TensorBoard */}
              {sweepTensorboardArtifact && (
                <Box
                  onClick={() => handleDownload(sweepTensorboardArtifact.s3_key)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: '#0a0a0a',
                    border: '1px solid rgba(255,255,255,0.08)',
                    p: 2.5,
                    transition: 'border-color 0.15s',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.2)' },
                  }}
                >
                  <img src="/tensorboard-logo.png" alt="" style={{ width: 24, height: 24, marginBottom: 12 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: '-0.01em', mb: 0.5 }}>
                    TensorBoard
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    Download tfevents
                  </Typography>
                </Box>
              )}
              {/* Checkpoint / Download Policy */}
              {sweepNearestCheckpoint ? (
                <Box
                  onClick={() => handleDownload(sweepNearestCheckpoint.s3_key)}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: '#0a0a0a',
                    border: '1px solid rgba(255,255,255,0.08)',
                    p: 2.5,
                    transition: 'border-color 0.15s',
                    '&:hover': { borderColor: 'rgba(255,255,255,0.2)' },
                  }}
                >
                  <DownloadIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.5)', mb: 1.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: '-0.01em', mb: 0.5 }}>
                    Download Policy
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    {formatBytes(sweepNearestCheckpoint.size_bytes)}
                    {sweepNearestCpIter != null && ` · Iteration ${sweepNearestCpIter}`}
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>

          {/* Config */}
          <Card sx={{ backgroundColor: '#0a0a0a', border: '1px solid rgba(255,255,255,0.12)' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Config
              </Typography>
              {run.config.training_config && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {Object.entries(run.config.training_config)
                    .filter(([_, v]) => v != null)
                    .map(([k, v]) => (
                      <Typography key={k} variant="body2" color="text.secondary">
                        {k}:{' '}
                        <strong>
                          {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                        </strong>
                      </Typography>
                    ))}
                </Box>
              )}
              {run.config.sweep_configs && run.config.sweep_configs.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Sweep Parameters
                  </Typography>
                  {run.config.sweep_configs.map((sc) => (
                    <Typography key={sc.parameter} variant="body2" color="text.secondary">
                      {sc.parameter}: [{sc.values.join(', ')}]
                    </Typography>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </>)}

        {/* Pause confirmation dialog */}
        <Dialog
          open={pauseDialogOpen}
          onClose={() => setPauseDialogOpen(false)}
          PaperProps={{
            sx: {
              backgroundColor: '#0a0a0a',
              border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 2,
              minWidth: 440,
            },
          }}
        >
          <DialogTitle sx={{ color: '#fff', fontWeight: 600, fontSize: 18, pb: 1 }}>
            Pause training?
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.5 }}>
              Workers will be stopped. You can resume from the last checkpoint.
            </Typography>
            {lastCheckpointIter != null ? (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                Last checkpoint: <strong style={{ color: '#51cf66' }}>iteration {lastCheckpointIter}</strong>
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: '#ffa726' }}>
                No checkpoint saved yet. Progress will be lost if you pause now.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
            <Button
              onClick={() => setPauseDialogOpen(false)}
              sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'none', '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' } }}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePause}
              disabled={pausing}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#ffca28',
                color: '#000',
                '&:hover': { backgroundColor: '#ffc107' },
              }}
            >
              {pausing ? 'Pausing...' : 'Pause training'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // --- Render: Single run (interactive timeline) ---

  return (
    <Box
      sx={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        '& .MuiTypography-root, & .MuiButton-root, & .MuiChip-label': {
          fontFamily: 'inherit',
        },
      }}
    >
      {/* Paused status bar + resume */}
      {isPaused && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Chip
            label="paused"
            size="small"
            sx={{ backgroundColor: '#ffca28', color: '#000', fontWeight: 600 }}
          />
          <Typography variant="body2" color="text.secondary">
            {training ? `Paused at iteration ${training.current_iteration}/${training.max_iterations}` : 'Paused'}
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={handleResume}
            disabled={resuming}
            sx={{ ml: 'auto', backgroundColor: '#51cf66', color: '#000', fontWeight: 600, '&:hover': { backgroundColor: '#40c057' } }}
          >
            {resuming ? 'Resuming...' : 'Resume'}
          </Button>
        </Box>
      )}

      {/* Progress bar + pause/cancel */}
      {isActive && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{ height: 4, borderRadius: 2 }}
            />
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PauseCircleOutlineIcon />}
            onClick={() => setPauseDialogOpen(true)}
            disabled={pausing}
            sx={{ flexShrink: 0, borderColor: '#ffca28', color: '#ffca28', '&:hover': { borderColor: '#ffc107', backgroundColor: 'rgba(255,202,40,0.08)' } }}
          >
            Pause
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<CancelOutlinedIcon />}
            onClick={handleCancel}
            disabled={cancelling}
            sx={{ flexShrink: 0, borderColor: '#ff6b6b', color: '#ff6b6b', '&:hover': { borderColor: '#ff4444', backgroundColor: 'rgba(255,107,107,0.08)' } }}
          >
            {cancelling ? 'Cancelling...' : 'Cancel'}
          </Button>
        </Box>
      )}

      {/* Starting up state — no metrics yet */}
      {isActive && !hasTrainingData && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
          }}
        >
          <CircularProgress
            size={40}
            sx={{
              color: '#EF8997',
              mb: 2.5,
            }}
          />
          <Typography variant="body1" sx={{ fontWeight: 600, color: '#fff', mb: 0.5 }}>
            Starting up
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            Booting Isaac Sim and initializing environment...
          </Typography>
        </Box>
      )}

      {/* Failed banner — show error from first failed task */}
      {run.status === 'failed' && failedTaskErrors.length > 0 && (
        <Box
          sx={{
            p: 2.5,
            mb: 3,
            backgroundColor: 'rgba(255, 107, 107, 0.06)',
            border: '1px solid rgba(255, 107, 107, 0.25)',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff6b6b', mb: 0.5 }}>
            Run failed
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {failedTaskErrors[0].error}
          </Typography>
        </Box>
      )}

      {/* Download button — completed or paused + has checkpoint */}
      {(isCompleted || isPaused) && latestCheckpoint && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={() => handleDownload(latestCheckpoint.s3_key)}
            sx={{
              backgroundColor: '#51cf66',
              color: '#000',
              fontWeight: 600,
              '&:hover': { backgroundColor: '#40c057' },
            }}
          >
            Download Policy
          </Button>
        </Box>
      )}

      {/* Stats Row — only show once we have data */}
      {(hasTrainingData || !isActive) && (
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {isActive ? (
          <>
            <StatCard
              value={(() => {
                const h = Math.floor(elapsedMs / 3600000);
                const m = Math.floor((elapsedMs % 3600000) / 60000);
                return h > 0 ? `${h}h ${m}m` : `${Math.max(0, m)}m`;
              })()}
              label="Time Running"
            />
            <StatCard
              value={progressPercent > 0 ? `${progressPercent.toFixed(0)}%` : '--'}
              label="Iteration"
            />
          </>
        ) : (
          <>
            <StatCard
              value={new Date(run.created_at).toLocaleDateString('en-US', {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
              label="Started"
            />
            <StatCard
              value={(() => {
                const h = elapsedMs / 3600000;
                return h >= 1 ? `${h.toFixed(1)}h` : `${Math.max(0, Math.round(h * 60))}m`;
              })()}
              label="Train Time"
            />
          </>
        )}
        <StatCard
          value={training?.mean_reward?.toFixed(2) ?? '--'}
          label="Mean Reward"
        />
        <StatCard
          value={training?.mean_episode_length?.toFixed(0) ?? '--'}
          label="Ep Length"
        />
        <StatCard
          value={`$${costSoFar.toFixed(2)}`}
          label={isActive ? 'Cost' : 'Total Cost'}
        />
        {isActive && etaDisplay && (
          <StatCard
            value={etaDisplay}
            label="ETA"
          />
        )}
      </Box>
      )}

      {/* Interactive Reward Chart + Video + Config — hidden during startup */}
      {(hasTrainingData || !isActive) && (<>
      <Card
        sx={{
          backgroundColor: '#0a0a0a',
          border: '1px solid rgba(255,255,255,0.12)',
          mb: 0.5,
        }}
      >
        <CardContent sx={{ pb: 1 }}>
          <Box sx={{ width: '100%', height: 360 }}>
            <ResponsiveContainer>
              <LineChart data={rewardHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="iteration"
                  stroke="rgba(255,255,255,0.4)"
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  yAxisId="reward"
                  stroke="rgba(255,255,255,0.4)"
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  yAxisId="epLen"
                  orientation="right"
                  stroke="rgba(255,255,255,0.2)"
                  tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.3)' }}
                />
                <RechartsTooltip
                  content={
                    <ChartTooltip
                      videoIterations={iterMap.videoIterations}
                      checkpointIterations={iterMap.checkpointIterations}
                      pendingEvalIterations={pendingEvalIters}
                    />
                  }
                />

                {/* Reward line */}
                <Line
                  type="linear"
                  dataKey="mean_reward"
                  yAxisId="reward"
                  stroke="#fff"
                  dot={false}
                  strokeWidth={2}
                  name="Mean Reward"
                  isAnimationActive={false}
                />

                {/* Episode length line */}
                <Line
                  type="linear"
                  dataKey="mean_episode_length"
                  yAxisId="epLen"
                  stroke="#82ca9d"
                  dot={false}
                  strokeWidth={1.5}
                  strokeOpacity={0.5}
                  name="Ep Length"
                  isAnimationActive={false}
                />

                {/* Selected iteration vertical line */}
                {selectedIteration !== null && (
                  <ReferenceLine
                    x={selectedIteration}
                    stroke="rgba(255,255,255,0.3)"
                    strokeDasharray="4 4"
                  />
                )}

                {/* Checkpoint markers */}
                {iterMap.checkpointIterations.map((iter) => (
                  <ReferenceDot
                    key={`cp-${iter}`}
                    x={iter}
                    y={iterMap.rewardAtIteration(iter)}
                    yAxisId="reward"
                    r={5}
                    fill="#51cf66"
                    stroke="none"
                  />
                ))}

                {/* Video markers */}
                {iterMap.videoIterations.map((iter) => (
                  <ReferenceDot
                    key={`vid-${iter}`}
                    x={iter}
                    y={iterMap.rewardAtIteration(iter)}
                    yAxisId="reward"
                    r={4}
                    fill="#6bcfff"
                    stroke="none"
                  />
                ))}

                {/* Pending eval markers (pulsing) */}
                {pendingEvalIters
                  .filter((iter) => !iterMap.videoIterations.includes(iter))
                  .map((iter) => (
                  <ReferenceDot
                    key={`pending-${iter}`}
                    x={iter}
                    y={iterMap.rewardAtIteration(iter)}
                    yAxisId="reward"
                    r={4}
                    fill="#6bcfff"
                    fillOpacity={0.4}
                    stroke="#6bcfff"
                  >
                    <animate attributeName="r" values="4;7;4" dur="1.5s" repeatCount="indefinite" />
                  </ReferenceDot>
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 3, ml: 1, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 16, height: 2, backgroundColor: '#fff' }} />
              <Typography variant="caption" color="text.secondary">
                Reward
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 16, height: 2, backgroundColor: '#82ca9d', opacity: 0.5 }} />
              <Typography variant="caption" color="text.secondary">
                Ep Length
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#51cf66',
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Checkpoint
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#6bcfff',
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Video
              </Typography>
            </Box>
            {pendingEvalIters.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: '#6bcfff',
                    opacity: 0.4,
                    boxShadow: '0 0 4px #6bcfff',
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  Eval Pending
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Video + Action Cards */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 3 }}>
        {/* Video — 2/3 */}
        <Box sx={{ flex: '0 0 66%', minWidth: 0 }}>
          <Card
            sx={{
              backgroundColor: '#0a0a0a',
              border: '1px solid rgba(255,255,255,0.08)',
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: '0 !important' }}>
              {videoUrl ? (
                <video
                  key={videoUrl}
                  src={videoUrl}
                  controls
                  autoPlay
                  muted
                  loop
                  style={{ width: '100%', display: 'block' }}
                />
              ) : pendingEvalIters.length > 0 ? (
                <Box
                  sx={{
                    aspectRatio: '16/9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.02)',
                  }}
                >
                  <CircularProgress size={28} sx={{ color: '#6bcfff', mr: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    Rendering eval video...
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    aspectRatio: '16/9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255,255,255,0.02)',
                  }}
                >
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', fontWeight: 500 }}>
                    {isActive ? 'Waiting for video...' : 'No videos'}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Video navigation */}
          {iterMap.videoIterations.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
              <IconButton
                size="small"
                onClick={goToPrevVideo}
                onMouseEnter={preloadAdjacentVideos}
                disabled={currentVideoIndex <= 0}
                sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, textAlign: 'center' }}>
                {currentVideoIndex >= 0
                  ? `${currentVideoIndex + 1} / ${iterMap.videoIterations.length}`
                  : `${iterMap.videoIterations.length} videos`}
              </Typography>
              <IconButton
                size="small"
                onClick={goToNextVideo}
                onMouseEnter={preloadAdjacentVideos}
                disabled={currentVideoIndex >= iterMap.videoIterations.length - 1}
                sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
              {nearestVideoIter !== null && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', ml: 1 }}>
                  {nearestVideoIter === selectedIteration
                    ? `Iteration ${nearestVideoIter}`
                    : `Nearest at iteration ${nearestVideoIter}`}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Action cards — 1/3 */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* W&B */}
          {run.wandb_url && (
            <Box
              component="a"
              href={run.wandb_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                backgroundColor: '#0a0a0a',
                border: '1px solid rgba(255,255,255,0.08)',
                p: 2.5,
                transition: 'border-color 0.15s',
                '&:hover': { borderColor: 'rgba(255,255,255,0.2)' },
                display: 'block',
              }}
            >
              <img src="/wandb-logo.png" alt="" style={{ width: 24, height: 24, marginBottom: 12 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: '-0.01em', mb: 0.5 }}>
                Weights & Biases
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                View run dashboard
              </Typography>
            </Box>
          )}
          {/* TensorBoard */}
          {tensorboardArtifact && (
            <Box
              onClick={() => handleDownload(tensorboardArtifact.s3_key)}
              sx={{
                cursor: 'pointer',
                backgroundColor: '#0a0a0a',
                border: '1px solid rgba(255,255,255,0.08)',
                p: 2.5,
                transition: 'border-color 0.15s',
                '&:hover': { borderColor: 'rgba(255,255,255,0.2)' },
              }}
            >
              <img src="/tensorboard-logo.png" alt="" style={{ width: 24, height: 24, marginBottom: 12 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: '-0.01em', mb: 0.5 }}>
                TensorBoard
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                Download tfevents
              </Typography>
            </Box>
          )}

          {/* Checkpoint / Download Policy */}
          {nearestCheckpoint ? (
            <Box
              onClick={() => handleDownload(nearestCheckpoint.s3_key)}
              sx={{
                cursor: 'pointer',
                backgroundColor: '#0a0a0a',
                border: '1px solid rgba(255,255,255,0.08)',
                p: 2.5,
                transition: 'border-color 0.15s',
                '&:hover': { borderColor: 'rgba(255,255,255,0.2)' },
              }}
            >
              <DownloadIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.5)', mb: 1.5 }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  mb: 0.5,
                }}
              >
                Download Policy
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                {formatBytes(nearestCheckpoint.size_bytes)}
                {nearestCpIter != null && ` · Iteration ${nearestCpIter}`}
              </Typography>
            </Box>
          ) : null}
        </Box>
      </Box>

      {/* Config */}
      <Card sx={{ backgroundColor: '#0a0a0a', border: '1px solid rgba(255,255,255,0.12)' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Config
          </Typography>
          {run.config.training_config && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {Object.entries(run.config.training_config)
                .filter(([_, v]) => v != null)
                .map(([k, v]) => (
                  <Typography key={k} variant="body2" color="text.secondary">
                    {k}:{' '}
                    <strong>
                      {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </strong>
                  </Typography>
                ))}
            </Box>
          )}
        </CardContent>
      </Card>
      </>)}

      {/* Pause confirmation dialog */}
      <Dialog
        open={pauseDialogOpen}
        onClose={() => setPauseDialogOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: '#0a0a0a',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 2,
            minWidth: 440,
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff', fontWeight: 600, fontSize: 18, pb: 1 }}>
          Pause training?
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.5 }}>
            Workers will be stopped. You can resume from the last checkpoint.
          </Typography>
          {lastCheckpointIter != null ? (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              Last checkpoint: <strong style={{ color: '#51cf66' }}>iteration {lastCheckpointIter}</strong>
            </Typography>
          ) : (
            <Typography variant="body2" sx={{ color: '#ffa726' }}>
              No checkpoint saved yet. Progress will be lost if you pause now.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
          <Button
            onClick={() => setPauseDialogOpen(false)}
            sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'none', '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePause}
            disabled={pausing}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#ffca28',
              color: '#000',
              '&:hover': { backgroundColor: '#ffc107' },
            }}
          >
            {pausing ? 'Pausing...' : 'Pause training'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingRunDetails;
